import { useState } from 'react';
import { InfoToggle } from './InfoToggle';
import { SliderRow } from './SliderRow';
import { StatBox } from './StatBox';
import { FadeUp } from '../primitives/FadeUp';
import { SectionH2 } from '../primitives/SectionH2';
import { SectionLabel } from '../primitives/SectionLabel';
import { useCountUp } from '../../hooks/useCountUp';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ACCENT, BORDER, FONT_ACCENT, FONT_MONO, MUTED, SURFACE2, TEXT_SECONDARY } from '../../tokens';

const GREEN = '#22c55e';

/** Weeks worked per year, after four weeks of leave. */
const WEEKS_PER_YEAR = 48;
/** What the owner's week comes down to once Content OS is running. */
const HOURS_AFTER = 2;
/** Committed monthly output. */
const PIECES_PER_MONTH = 43;

/** Which stat an open InfoToggle is currently highlighting. */
type Highlight = 'content' | 'chasing' | 'output' | null;

/** Strips formatting characters so typed input like "12 hrs" parses. */
const parseInteger = (text: string): number => parseInt(text.replace(/[^0-9]/g, ''), 10);

/** Whole hours with a thousands separator. */
const formatHours = (n: number): string => Math.round(n).toLocaleString();

export function ROICalculator() {
  const [contentHours, setContentHours] = useState(12);
  const [chasingHours, setChasingHours] = useState(4);
  const [highlighted, setHighlighted] = useState<Highlight>(null);
  const isMobile = useIsMobile();

  const currentWeekly = contentHours + chasingHours;
  // Never show a negative saving if someone sets the sliders below two hours.
  const reclaimedWeekly = Math.max(0, currentWeekly - HOURS_AFTER);
  const reclaimedYearly = reclaimedWeekly * WEEKS_PER_YEAR;
  const piecesYearly = PIECES_PER_MONTH * 12;

  const animReclaimedWeekly = useCountUp(reclaimedWeekly);
  const animReclaimedYearly = useCountUp(reclaimedYearly);
  const animPieces = useCountUp(piecesYearly);

  return (
    <section style={{ padding: isMobile ? '60px 20px' : '100px 64px', background: 'rgba(8,8,8,0.88)' }}>
      <div style={{ maxWidth: 940, margin: '0 auto' }}>
        <FadeUp>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <SectionLabel number="05">The Two Hour Week</SectionLabel>
            <SectionH2 style={{ marginBottom: 14 }}>
              How many hours a week is your marketing actually taking?
            </SectionH2>
            <p style={{ color: TEXT_SECONDARY, maxWidth: 520, margin: '0 auto', lineHeight: 1.75 }}>
              Count the drafting, the resizing, the scheduling, the chasing. Then look at what is left
              when the only part you keep is the part that needed you in the first place.
            </p>
          </div>
        </FadeUp>

        <FadeUp delay={80}>
          <div
            style={{
              background: SURFACE2,
              border: `1px solid ${BORDER}`,
              borderRadius: 24,
              padding: isMobile ? 24 : 36,
              marginBottom: 14,
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: isMobile ? 28 : 32,
              }}
            >
              <div>
                <InfoToggle
                  title="What counts as content time"
                  onHover={() => setHighlighted('content')}
                  onLeave={() => setHighlighted(null)}
                >
                  <p style={{ margin: '0 0 8px' }}>
                    Writing posts and captions, making graphics, resizing for each platform, scheduling,
                    and staring at a blank page deciding what to post.
                  </p>
                  <p style={{ margin: 0, fontSize: '0.72rem', color: MUTED }}>
                    Owners doing this themselves typically land between{' '}
                    <strong style={{ color: ACCENT }}>15 and 20 hours a week</strong>.
                  </p>
                </InfoToggle>
                <SliderRow
                  label="Content and posting"
                  value={contentHours}
                  min={0}
                  max={30}
                  format={(v) => `${v} hrs/wk`}
                  parse={parseInteger}
                  onChange={setContentHours}
                />
              </div>

              <div>
                <InfoToggle
                  title="What counts as chasing"
                  onHover={() => setHighlighted('chasing')}
                  onLeave={() => setHighlighted(null)}
                >
                  <p style={{ margin: '0 0 8px' }}>
                    Replying to enquiries, following up the ones that went quiet, asking for reviews, and
                    answering the same handful of questions on every call.
                  </p>
                  <p style={{ margin: 0, fontSize: '0.72rem', color: MUTED }}>
                    Contact odds drop more than{' '}
                    <strong style={{ color: ACCENT }}>tenfold within the first hour</strong>, so this work
                    is worth the most exactly when you have the least time for it.
                  </p>
                </InfoToggle>
                <SliderRow
                  label="Lead chasing and reviews"
                  value={chasingHours}
                  min={0}
                  max={20}
                  format={(v) => `${v} hrs/wk`}
                  parse={parseInteger}
                  onChange={setChasingHours}
                />
              </div>
            </div>

            <p
              style={{
                marginTop: 24,
                fontFamily: FONT_MONO,
                fontSize: '0.65rem',
                color: '#444',
                letterSpacing: '0.06em',
              }}
            >
              * Assumes a two hour weekly commitment from you once the system is live, over 48 working
              weeks. Your two hours are review, approval, and a few phone clips from the job.
            </p>
          </div>

          {/* Your week, before and after */}
          <div
            style={{
              background: 'rgba(34,197,94,0.06)',
              border: `1px solid ${highlighted ? 'rgba(34,197,94,0.7)' : 'rgba(34,197,94,0.25)'}`,
              boxShadow: highlighted ? '0 0 28px rgba(34,197,94,0.15)' : 'none',
              borderRadius: 18,
              padding: '24px 28px',
              marginBottom: 14,
              transition: 'border-color 0.3s, box-shadow 0.3s',
            }}
          >
            <div
              style={{
                fontFamily: FONT_MONO,
                fontSize: '0.62rem',
                color: GREEN,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                marginBottom: 16,
              }}
            >
              Your Week, Before And After
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr auto 1fr',
                gap: isMobile ? 12 : 24,
                alignItems: 'center',
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: FONT_ACCENT,
                    fontStyle: 'italic',
                    fontSize: '1.9rem',
                    fontWeight: 400,
                    color: TEXT_SECONDARY,
                    marginBottom: 4,
                  }}
                >
                  {currentWeekly} hrs
                </div>
                <div style={{ fontSize: '0.75rem', color: MUTED }}>On marketing right now</div>
              </div>

              <div
                aria-hidden="true"
                style={{ textAlign: 'center', color: GREEN, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>

              <div>
                <div
                  style={{
                    fontFamily: FONT_ACCENT,
                    fontStyle: 'italic',
                    fontSize: '1.9rem',
                    fontWeight: 400,
                    color: GREEN,
                    marginBottom: 4,
                  }}
                >
                  {HOURS_AFTER} hrs
                </div>
                <div style={{ fontSize: '0.75rem', color: TEXT_SECONDARY }}>Review, approval, phone clips</div>
              </div>
            </div>

            <div
              style={{
                marginTop: 16,
                paddingTop: 16,
                borderTop: '1px solid rgba(34,197,94,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <span style={{ fontSize: '0.82rem', color: TEXT_SECONDARY }}>Hours back over a year</span>
              <span style={{ fontFamily: FONT_ACCENT, fontStyle: 'italic', fontSize: '1.4rem', color: GREEN }}>
                {formatHours(animReclaimedYearly)} hrs
              </span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: 12 }}>
            <StatBox
              glow={highlighted === 'content' || highlighted === 'chasing'}
              label="Hours back / week"
              value={`${formatHours(animReclaimedWeekly)} hrs`}
            />
            <StatBox
              glow={highlighted === 'content' || highlighted === 'chasing'}
              label="Hours back / year"
              value={`${formatHours(animReclaimedYearly)} hrs`}
            />
            <StatBox glow={highlighted === 'output'} label="Pieces published / year" value={formatHours(animPieces)} accent />
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
